import { API_BASE } from "@/lib/constants";

export interface PlatformStats {
  total_users: number;
  verified_users: number;
  active_today: number;
  active_this_week: number;
  total_credits_used: number;
  total_workspaces: number;
  suspended_users: number;
  new_users_today: number;
  new_users_this_week: number;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  email_verified: boolean;
  plan: string;
  credits_remaining: number;
  suspended: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface AdminCreditTransaction {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  type: string;
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
}

export async function getAdminStats(): Promise<PlatformStats> {
  const res = await fetch(`${API_BASE}/api/admin/stats`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load admin stats");
  return res.json();
}

export async function getAdminUsers(params: { search?: string; page?: number; limit?: number }): Promise<{
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
}> {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  const res = await fetch(`${API_BASE}/api/admin/users?${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load users");
  return res.json();
}

export async function getAdminUserDetail(id: string): Promise<{
  user: AdminUser;
  transactions: AdminCreditTransaction[];
}> {
  const res = await fetch(`${API_BASE}/api/admin/users/detail?id=${id}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load user detail");
  return res.json();
}

export async function adminUpdateCredits(userId: string, amount: number, description?: string): Promise<{ success: boolean; user: AdminUser }> {
  const res = await fetch(`${API_BASE}/api/admin/users/credits`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ user_id: userId, amount, description }),
  });
  if (!res.ok) throw new Error("Failed to update credits");
  return res.json();
}

export async function adminUpdatePlan(userId: string, plan: string): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/api/admin/users/plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ user_id: userId, plan }),
  });
  if (!res.ok) throw new Error("Failed to update plan");
  return res.json();
}

export async function adminSuspendUser(userId: string, suspended: boolean): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/api/admin/users/suspend`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ user_id: userId, suspended }),
  });
  if (!res.ok) throw new Error("Failed to update user status");
  return res.json();
}

export async function adminImpersonate(userId: string): Promise<{ success: boolean; user: AdminUser }> {
  const res = await fetch(`${API_BASE}/api/admin/users/impersonate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ user_id: userId }),
  });
  if (!res.ok) throw new Error("Failed to impersonate user");
  return res.json();
}

export async function getAdminTransactions(params: { user_id?: string; page?: number; limit?: number }): Promise<{
  transactions: AdminCreditTransaction[];
  total: number;
  page: number;
  limit: number;
}> {
  const qs = new URLSearchParams();
  if (params.user_id) qs.set("user_id", params.user_id);
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  const res = await fetch(`${API_BASE}/api/admin/transactions?${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load transactions");
  return res.json();
}

export interface WorkspaceRevenue {
  workspace_id: string;
  workspace_name: string;
  slug: string;
  member_count: number;
  total_revenue: number; // in cents
  total_credits: number;
  payment_count: number;
  created_at: string;
}

export interface PlatformRevenue {
  total_revenue: number; // in cents
  payment_count: number;
}

export async function getAdminWorkspaceRevenue(params: { page?: number; limit?: number }): Promise<{
  workspaces: WorkspaceRevenue[];
  total: number;
  platform: PlatformRevenue;
  page: number;
  limit: number;
}> {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  const res = await fetch(`${API_BASE}/api/admin/revenue?${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load workspace revenue");
  return res.json();
}

export interface AdminAPIKey {
  id: string;
  key_prefix: string;
  name: string;
  is_active: boolean;
  disabled_by_admin: boolean;
  created_at: string;
  last_used_at: string | null;
  credits_used: number;
  creator_email: string;
}

export async function getAdminAPIKeys(): Promise<{ keys: AdminAPIKey[] }> {
  const res = await fetch(`${API_BASE}/api/admin/keys`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load API keys");
  return res.json();
}

export async function adminDisableKey(keyId: string, disabled: boolean): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/api/admin/keys/disable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ key_id: keyId, disabled }),
  });
  if (!res.ok) throw new Error("Failed to update key status");
  return res.json();
}

// ── Feedback ──────────────────────────────────────────────

export interface FeedbackStats {
  total_positive: number;
  total_negative: number;
  total: number;
}

export interface FeedbackEntry {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  endpoint: string;
  rating: "positive" | "negative";
  comment: string;
  created_at: string;
}

export async function getAdminFeedback(params: { user_id?: string; page?: number }): Promise<{
  stats: FeedbackStats;
  entries: FeedbackEntry[];
  total: number;
  page: number;
  limit: number;
}> {
  const qs = new URLSearchParams();
  if (params.user_id) qs.set("user_id", params.user_id);
  if (params.page) qs.set("page", String(params.page));
  const res = await fetch(`${API_BASE}/api/admin/feedback?${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load feedback");
  return res.json();
}

// ── Careers ──────────────────────────────────────────

export interface CareerPosition {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  salary_range: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function getAdminCareers(): Promise<CareerPosition[]> {
  const res = await fetch(`${API_BASE}/api/admin/careers`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load careers");
  return res.json();
}

export async function adminCreateCareer(data: {
  title: string;
  department: string;
  location: string;
  type: string;
  salary_range: string;
  description: string;
}): Promise<CareerPosition> {
  const res = await fetch(`${API_BASE}/api/admin/careers/create`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create position");
  return res.json();
}

export async function adminUpdateCareer(data: {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  salary_range: string;
  description: string;
  is_active: boolean;
}): Promise<CareerPosition> {
  const res = await fetch(`${API_BASE}/api/admin/careers/update`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update position");
  return res.json();
}

export async function adminDeleteCareer(id: string): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/api/admin/careers/delete`, {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
  if (!res.ok) throw new Error("Failed to delete position");
  return res.json();
}

export async function getPublicCareers(): Promise<CareerPosition[]> {
  const res = await fetch(`${API_BASE}/api/content/careers`);
  if (!res.ok) throw new Error("Failed to load careers");
  return res.json();
}

export async function getPublicCareerPosition(id: string): Promise<CareerPosition> {
  const res = await fetch(`${API_BASE}/api/content/careers/position?id=${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error("Failed to load position");
  return res.json();
}

// Build a human-readable URL slug for a role, e.g.
// "Dark-Web-Intelligence-Product-Manager-june-2026".
export function careerSlug(p: Pick<CareerPosition, "title" | "created_at">): string {
  const titlePart = p.title
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
  const d = new Date(p.created_at);
  const month = Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("en-US", { month: "long", timeZone: "UTC" }).toLowerCase();
  const year = Number.isNaN(d.getTime()) ? "" : d.getUTCFullYear();
  const suffix = month && year ? `-${month}-${year}` : "";
  return `${titlePart}${suffix}`;
}

export async function getPublicCareerBySlug(slug: string): Promise<CareerPosition> {
  const all = await getPublicCareers();
  const match = all.find(
    (p) => careerSlug(p).toLowerCase() === slug.toLowerCase()
  );
  if (!match) throw new Error("Failed to load position");
  return match;
}

export interface CareerApplication {
  id: string;
  position_id: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  linkedin: string;
  twitter: string;
  website: string;
  resume_url: string;
  cover_note: string;
  created_at: string;
}

export async function getAdminCareerApplications(positionId?: string): Promise<CareerApplication[]> {
  const qs = positionId ? `?position_id=${encodeURIComponent(positionId)}` : "";
  const res = await fetch(`${API_BASE}/api/admin/careers/applications${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load applications");
  return res.json();
}

// ── Blog ─────────────────────────────────────────────

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  tag: string;
  author_name: string;
  author_title: string;
  author_avatar_url: string;
  cover_image_url: string;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export async function getAdminBlogs(): Promise<BlogPost[]> {
  const res = await fetch(`${API_BASE}/api/admin/blog`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load posts");
  return res.json();
}

// Uploads a blog image (author avatar or cover) and returns a public proxy URL.
export async function uploadBlogImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_BASE}/api/admin/blog/upload`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to upload image");
  return data.url as string;
}

export async function adminCreateBlog(data: {
  title: string;
  excerpt: string;
  content: string;
  tag: string;
  author_name: string;
  author_title: string;
  author_avatar_url: string;
  cover_image_url: string;
  published: boolean;
}): Promise<BlogPost> {
  const res = await fetch(`${API_BASE}/api/admin/blog/create`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create post");
  return res.json();
}

export async function adminUpdateBlog(data: {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  tag: string;
  author_name: string;
  author_title: string;
  author_avatar_url: string;
  cover_image_url: string;
  published: boolean;
}): Promise<BlogPost> {
  const res = await fetch(`${API_BASE}/api/admin/blog/update`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update post");
  return res.json();
}

export async function adminDeleteBlog(id: string): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/api/admin/blog/delete`, {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
  if (!res.ok) throw new Error("Failed to delete post");
  return res.json();
}

// The content API returns image paths under the legacy /api/blog/image/ prefix,
// which is no longer proxied to the backend; rewrite them to the /api/content/
// path that is. No-op on URLs already using the new prefix or absolute URLs.
function normalizeBlogImages(post: BlogPost): BlogPost {
  const fix = (u: string) => u.replace(/^\/api\/blog\/image\//, "/api/content/blog/image/");
  return {
    ...post,
    cover_image_url: post.cover_image_url ? fix(post.cover_image_url) : post.cover_image_url,
    author_avatar_url: post.author_avatar_url ? fix(post.author_avatar_url) : post.author_avatar_url,
  };
}

export async function getPublicBlogs(params?: { tag?: string; limit?: number; offset?: number }): Promise<{
  posts: BlogPost[];
  total: number;
}> {
  const qs = new URLSearchParams();
  if (params?.tag) qs.set("tag", params.tag);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  const res = await fetch(`${API_BASE}/api/content/blog?${qs}`);
  if (!res.ok) throw new Error("Failed to load posts");
  const data = await res.json();
  return { ...data, posts: (data.posts ?? []).map(normalizeBlogImages) };
}

export async function getPublicBlogPost(slug: string): Promise<BlogPost> {
  const res = await fetch(`${API_BASE}/api/content/blog/post?slug=${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error("Failed to load post");
  return normalizeBlogImages(await res.json());
}

export async function submitCareerApplication(data: {
  position_id: string;
  name: string;
  email: string;
  phone: string;
  location?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  resume_url?: string;
  cover_note?: string;
}): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/api/content/careers/apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Failed to submit application" }));
    throw new Error(err.error || "Failed to submit application");
  }
  return res.json();
}

// ── AI Operations ──────────────────────────────────────────

export interface ServiceProvider {
  name: string;
  slug: string;
  category: string;
  cost_per_call: number;
  rate_limit: number;
  rate_window: string;
  plan_name: string;
  credits_total: number;
  credits_used: number;
  is_self_hosted: boolean;
  backup_for: string;
  status: "operational" | "degraded" | "down";
  status_msg: string;
  used_today: number;
  used_this_week: number;
  used_this_month: number;
  avg_latency_ms: number;
  error_rate: number;
}

export interface OpsStats {
  total_calls_today: number;
  total_calls_month: number;
  total_cost_today: number;
  total_cost_month: number;
  total_revenue_today: number;
  total_revenue_month: number;
  avg_latency_ms: number;
  overall_error_rate: number;
}

export interface OpsResponse {
  stats: OpsStats;
  services: ServiceProvider[];
}

export async function getAdminOps(): Promise<OpsResponse> {
  const res = await fetch(`${API_BASE}/api/admin/ops`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load ops data");
  return res.json();
}

// ── Lookup Analytics ──────────────────────────────────

export interface LookupUserStat {
  user_id: string;
  email: string;
  name: string;
  count: number;
  credits: number;
}

export interface LookupTypeStat {
  type: string;
  count: number;
  credits: number;
  errors: number;
  unique_users: number;
  top_users: LookupUserStat[];
}

export interface LookupTrendPoint {
  date: string;
  counts: Record<string, number>;
  total: number;
}

export interface LookupsOverview {
  days: number;
  total_lookups: number;
  total_credits: number;
  unique_users: number;
  types: LookupTypeStat[];
  top_users: LookupUserStat[];
  trends: LookupTrendPoint[];
}

export async function getAdminLookups(days = 30): Promise<LookupsOverview> {
  const res = await fetch(`${API_BASE}/api/admin/lookups?days=${days}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load lookup analytics");
  return res.json();
}

export interface UserLookupBreakdown {
  user: { id: string; email: string; name: string };
  days: number;
  total_lookups: number;
  total_credits: number;
  types: LookupTypeStat[];
}

export async function getAdminUserLookups(userId: string, days = 30): Promise<UserLookupBreakdown> {
  const res = await fetch(`${API_BASE}/api/admin/lookups/user?id=${encodeURIComponent(userId)}&days=${days}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load user lookup breakdown");
  return res.json();
}

// ── Email lookup observability ─────────────────────────

export interface AdminEmailEvent {
  id: string;
  time: string;
  user_id: string;
  user_email: string;
  user_name: string;
  email: string;
  type: string;
  source: string;
  outcome: string;
  status_code: number;
  credits: number;
}

export interface AdminEmailFile {
  id: string;
  type: string;
  user_id: string;
  user_email: string;
  user_name: string;
  file_name: string;
  total: number;
  status: string;
  created_at: string;
  downloadable: boolean;
  sensitive: boolean;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export async function getAdminEmailEvents(params: {
  user_id?: string;
  search?: string;
  days?: number;
  page?: number;
  limit?: number;
}): Promise<Paginated<AdminEmailEvent>> {
  const qs = new URLSearchParams();
  if (params.user_id) qs.set("user_id", params.user_id);
  if (params.search) qs.set("search", params.search);
  if (params.days) qs.set("days", String(params.days));
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  const res = await fetch(`${API_BASE}/api/admin/lookups/email/events?${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load email lookups");
  return res.json();
}

export async function getAdminEmailFiles(params: {
  user_id?: string;
  days?: number;
  page?: number;
  limit?: number;
}): Promise<Paginated<AdminEmailFile>> {
  const qs = new URLSearchParams();
  if (params.user_id) qs.set("user_id", params.user_id);
  if (params.days) qs.set("days", String(params.days));
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  const res = await fetch(`${API_BASE}/api/admin/lookups/email/files?${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load uploaded files");
  return res.json();
}

// adminEmailFileDownloadUrl returns the URL that redirects to a presigned S3
// download. Password uploads are never downloadable (server returns 403).
export function adminEmailFileDownloadUrl(type: string, id: string): string {
  return `${API_BASE}/api/admin/lookups/email/file-download?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`;
}

// ── Cache Management ──────────────────────────────────

export interface RedisCacheStats {
  total_keys: number;
  lookup_keys: number;
  search_keys: number;
  rate_limit_keys: number;
  memory_used: string;
}

export interface PostgresCacheStats {
  lookup_count: number;
  lookup_size: string;
  search_cache_count: number;
  search_cache_size: string;
  oldest_lookup: string;
  oldest_search: string;
}

export interface CacheStatsResponse {
  redis: RedisCacheStats | { status: string } | { error: string };
  postgres: PostgresCacheStats | null;
}

export async function getAdminCacheStats(): Promise<CacheStatsResponse> {
  const res = await fetch(`${API_BASE}/api/admin/cache`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load cache stats");
  return res.json();
}

export async function adminClearCache(action: string, opts?: { email?: string; type?: string }): Promise<{ action: string; deleted: number; email?: string; type?: string }> {
  const res = await fetch(`${API_BASE}/api/admin/cache`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ action, ...opts }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    throw new Error(err.error || "Failed to clear cache");
  }
  return res.json();
}
